import React, { useRef, useEffect, useState } from "react";
import { Caret } from "@cmsgov/ds-medicare-gov";
import Collapsible from "react-collapsible";
import { FormattedMessage } from "react-intl-phraseapp";
import { sendTealiumEvent } from "../helpers";
import { TealiumEventProps } from "../helpers/objectUtilities";

export interface CollapsibleBoxProps {
  label?: string | JSX.Element;
  labelId?: string;
  titleClassname?: string;
  containerClassname?: string;
  contentClassname?: string;
  caretClassname?: string;
  open?: boolean;
  onOpening?: () => void;
  onOpen?: () => void;
  onClosing?: () => void;
  tealiumProps?: TealiumEventProps;
}

const CollapsibleBox: React.FunctionComponent<CollapsibleBoxProps> = ({
  children,
  label,
  labelId,
  titleClassname,
  containerClassname,
  contentClassname,
  caretClassname,
  open,
  onOpening,
  onOpen,
  onClosing,
  tealiumProps,
}) => {
  const triggerRef = useRef<HTMLParagraphElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [hideContent, setHideContent] = useState(!open);

  const LabelContent = (): JSX.Element => {
    if (label) {
      return <span>{label}</span>;
    } else if (labelId) {
      return <FormattedMessage id={labelId} />;
    }

    return <span></span>;
  };
  const TriggerMessage: React.FunctionComponent<{ open?: boolean }> = ({
    open,
  }) => (
    <span ref={triggerRef}>
      <LabelContent />{" "}
      <span aria-hidden>
        {open ? (
          <Caret
            up
            className={`ds-u-valign--middle ds-u-margin-left--1 ${caretClassname ||
              ""}`}
          />
        ) : (
          <Caret
            className={`ds-u-valign--middle ds-u-margin-left--1 ${caretClassname ||
              ""}`}
          />
        )}
      </span>
    </span>
  );

  const toggleAriaExpanded = (value: string): void => {
    if (triggerRef.current && triggerRef.current.parentElement) {
      triggerRef.current.parentElement.setAttribute("aria-expanded", value);
    }
    setHideContent(value === "true" ? false : true);
  };

  const updateContentVisibility = (): void => {
    if (contentRef.current) {
      const outer = contentRef.current.closest(".Collapsible__contentOuter");
      if (outer && outer.clientHeight !== 0) {
        setHideContent(false);
      } else {
        setHideContent(true);
      }
    }
  };

  useEffect(() => {
    if (triggerRef.current && triggerRef.current.parentElement) {
      triggerRef.current.parentElement.setAttribute(
        "aria-expanded",
        open ? "true" : "false"
      );
    }

    // some components artificially hide the collapsible on different
    // breakpoints, so we want to make sure the content has the correct
    // viisibility on render and resize
    updateContentVisibility();

    window.addEventListener("resize", updateContentVisibility);
    return (): void =>
      window.removeEventListener("resize", updateContentVisibility);
  }, []);

  return (
    <Collapsible
      contentInnerClassName={contentClassname}
      contentOuterClassName={containerClassname}
      open={open}
      easing={"ease-in-out"}
      onOpening={(): void => {
        toggleAriaExpanded("true");
        if (onOpening) onOpening();
      }}
      onOpen={(): void => {
        if (onOpen) onOpen();
        if (tealiumProps) {
          sendTealiumEvent(tealiumProps.event_action, tealiumProps.event_label);
        }
      }}
      onClosing={(): void => {
        toggleAriaExpanded("false");
        if (onClosing) onClosing();
      }}
      tabIndex={0}
      trigger={<TriggerMessage />}
      triggerTagName="button"
      triggerWhenOpen={<TriggerMessage open />}
      triggerClassName={titleClassname}
      triggerOpenedClassName={titleClassname}
    >
      <div
        ref={contentRef}
        className={
          hideContent
            ? "Collapsible__hidden_content"
            : "Collapsible__visible_content"
        }
      >
        {children}
      </div>
    </Collapsible>
  );
};

export default CollapsibleBox;
